import { Logger } from "../lib/logger";
import {
  apiURL,
  environmentName,
  pseSupportedTemplates,
  shouldSendAnalytics,
  zapierURL,
} from "./config";
import {
  embedViewSchemaName,
  createEmbedViewEvent,
  EmbedViewEventOptions,
} from "./events";
import {
  App,
  AppSearchResult,
  ZapTemplatesFetchResult,
  ZapSuggestion,
} from "./types";

interface WebintentProps {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content?: string;
  provider?: string; // an app slug
  embedded?: string; // to be true when in an iframe
}

/**
 * If the Zap Template is supported by the Partner Specific Editor, return a full PSE URL.
 * Otherwise, return a full webintent URL.
 *
 * TODO: If we expand the PSE, update code to be clearer that the URL might not always be
 * an actual webintent URL. Overriding this property for now for the TPS-3270 experiment.
 * Dani Weisz (2024-06-17)
 */
function createFullWebintentUrl(
  suggestion: ZapSuggestion,
  webintentProps: WebintentProps,
) {
  let url;
  if (pseSupportedTemplates.has(suggestion.id)) {
    url = new URL("/partner/editor", zapierURL);
    url.searchParams.set("template", suggestion.id);
  } else {
    url = new URL(suggestion.webintent_url, zapierURL);
  }

  for (const [param, value] of Object.entries(webintentProps)) {
    if (value !== undefined && value !== null) {
      url.searchParams.set(param, value);
    }
  }
  url.searchParams.set("entry-point-location", "partner_embed");
  return url.href;
}

/**
 * Turn webintent URLs from a `/relative/path` into a full URL with UTMs and
 * proper webintent params.
 */
function mutateWithFullWebintentUrls(
  result: ZapTemplatesFetchResult,
  utms: WebintentProps,
): void {
  for (const item of result.results) {
    item.webintent_url = createFullWebintentUrl(item, utms);
  }
}

interface AppSearchOptions {
  hideSlugs?: string[];
  abortSignal?: AbortSignal;
}

export async function searchApps(
  query: string,
  { hideSlugs, abortSignal }: AppSearchOptions,
): Promise<AppSearchResult | null> {
  if (query === "") {
    return null;
  }
  const url = new URL("/elements/apps/", apiURL);
  url.searchParams.set("title_search", query);
  url.searchParams.set("limit", "7");
  if (hideSlugs && hideSlugs.length > 0) {
    url.searchParams.set("exclude_slugs", hideSlugs.join(","));
  }
  const resp = await fetch(url.href, { signal: abortSignal });
  const data = await resp.json();
  return data;
}

export async function fetchApp(slug: string): Promise<App> {
  const appsUrl = new URL(`/elements/apps/${slug}/`, apiURL);
  const resp = await fetch(appsUrl.href);
  return await resp.json();
}

export async function sendEmbedViewEvent(options: EmbedViewEventOptions) {
  try {
    const event = createEmbedViewEvent(options);
    if (shouldSendAnalytics) {
      const url = new URL("/api/v4/tracking/event/", zapierURL);
      await fetch(url.href, {
        method: "POST",
        // Don't cancel the request if the user leaves the page before it's
        // finished sending
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          subject: embedViewSchemaName,
          properties: event,
        }),
      });
    }
    if (environmentName === "local" || environmentName === "testing") {
      Logger.info("[event]", embedViewSchemaName, event);
    }
  } catch (error) {
    Logger.warn("Unable to emit event:", error);
  }
}

interface AppPairingOptions {
  hideSlugs?: string[];
  categoriesSlugs?: string[];
}

export async function fetchAppPairings(
  slug: string,
  options?: AppPairingOptions,
): Promise<App[]> {
  const servicePairsUrl = new URL(`/elements/services/${slug}/pairs/`, apiURL);
  servicePairsUrl.searchParams.set("limit", "20");
  if (options?.hideSlugs) {
    servicePairsUrl.searchParams.set(
      "exclude_slugs",
      options.hideSlugs.join(","),
    );
  }
  if (options?.categoriesSlugs) {
    servicePairsUrl.searchParams.set(
      "categories",
      options.categoriesSlugs.join(","),
    );
  }
  const resp = await fetch(servicePairsUrl.href);
  const { results } = await resp.json();
  return results;
}

export interface ZapTemplateOptions {
  hideSlugs?: string[];
  categoriesSlugs?: string[];
  abortSignal?: AbortSignal;
}

function sortServicesBySteps(data: ZapTemplatesFetchResult): void {
  for (const zt of data.results) {
    try {
      // Reverse the steps so that duplicate keys will leave the FIRST item
      // instead of the LAST (because we're iterating backwards)
      const serviceToIndexID = new Map(
        (zt.steps ?? [])
          .slice()
          .reverse()
          .map((step, idx) => [step.selected_api ?? 0, step.id ?? idx]),
      );
      zt.services.sort((serviceA, serviceB) => {
        const indexA = serviceToIndexID.get(serviceA.id) ?? 0;
        const indexB = serviceToIndexID.get(serviceB.id) ?? 0;
        if (indexA < indexB) return -1;
        if (indexA > indexB) return 1;
        return 0;
      });
    } catch (err) {
      Logger.error("Failed to sort Zap Template", zt);
    }
  }
}

const zapTemplateFields = [
  "id",
  "webintent_url",
  "title",
  "services",
  "description",
  "steps",
].join(",");

export async function fetchZapTemplates(
  appPair: string[],
  utmProps: WebintentProps,
  { abortSignal, categoriesSlugs, hideSlugs }: ZapTemplateOptions = {},
): Promise<ZapTemplatesFetchResult> {
  // Remove empty items from the array
  appPair = appPair.filter((x) => x);
  if (appPair.length === 0 && !categoriesSlugs?.length) {
    return { results: [] };
  }
  const url = new URL("/elements/zap-templates/", apiURL);
  url.searchParams.set("fields", zapTemplateFields);
  url.searchParams.set("limit", "25");
  url.searchParams.set("must_match_all_services", "true");
  url.searchParams.set("service_slugs", appPair.join(","));
  if (categoriesSlugs) {
    url.searchParams.set("categories", categoriesSlugs.join(","));
  }
  const resp = await fetch(url.href, { signal: abortSignal });
  const data: ZapTemplatesFetchResult = await resp.json();
  // Filter templates where a filtered app is included. In the future, API
  // should support app exclusions.
  const length = hideSlugs?.length ?? 0;
  if (length > 0) {
    data.results = data.results.filter((result) => {
      for (const service of result.services) {
        if (hideSlugs?.includes(service.slug)) {
          return false;
        }
      }
      return true;
    });
  }
  sortServicesBySteps(data);
  mutateWithFullWebintentUrls(data, utmProps);
  return data;
}

type ID = string | number;

export async function fetchZapTemplatesByIds(
  ids: ID[],
  utmProps: WebintentProps,
  { abortSignal }: { abortSignal?: AbortSignal } = {},
): Promise<ZapTemplatesFetchResult> {
  const url = new URL("/elements/zap-templates/", apiURL);
  url.searchParams.set("fields", zapTemplateFields);
  url.searchParams.set("ids", ids.join(","));
  url.searchParams.set("limit", String(ids.length));
  const resp = await fetch(url.href, { signal: abortSignal });
  const data: ZapTemplatesFetchResult = await resp.json();
  sortZapsByIdOrder(ids, data);
  sortServicesBySteps(data);
  mutateWithFullWebintentUrls(data, utmProps);
  return data;
}

function sortZapsByIdOrder(ids: ID[], data: ZapTemplatesFetchResult): void {
  const idToIndex = new Map(ids.map((id, index) => [String(id), index]));

  function getIndex(id: ID): number {
    return idToIndex.get(String(id)) ?? 0;
  }

  data.results.sort((a, b) => {
    return compare(getIndex(a.id), getIndex(b.id));
  });
}

function compare<T>(a: T, b: T): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

export async function fetchSlugAndSelectedAPI({
  clientID,
}: {
  clientID: string;
}): Promise<{ selectedAPI: string; slug: string }> {
  const url = new URL("/elements/client/", apiURL);
  url.searchParams.set("client_id", clientID);
  const resp = await fetch(url.href);
  if (!resp.ok) {
    throw new Error(resp.statusText);
  }
  const { selected_api, slug } = await resp.json();
  return { selectedAPI: selected_api, slug };
}

export async function fetchOidcEnabled(slug: string): Promise<boolean> {
  const url = new URL("/oidc/provider/", zapierURL);
  url.searchParams.set("slug", slug);
  const resp = await fetch(url.href);
  return resp.ok;
}

type GuessZapResponse = {
  title: string;
  prefilled_url: string;
  steps: GuessZapResponseStep[];
};

type GuessZapResponseStep = {
  step: {
    title: string;
    app: string;
    api: string;
  };
};

export type ZapGuessStep = {
  title: string;
  appName: string;
  selectedApi: string;
};

export type ZapGuess = {
  title: string;
  prefilledUrl: string;
  steps: ZapGuessStep[];
};

export async function guessZap({
  clientId,
  description,
  abortSignal,
}: {
  clientId: string;
  description: string;
  abortSignal?: AbortSignal;
}): Promise<ZapGuess> {
  const url = new URL("/v2/guess", apiURL);
  url.searchParams.set("client_id", clientId);
  const response = await fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ description }),
    signal: abortSignal,
  });
  if (!response.ok) {
    throw new Error(`POST ${url} ${response.status}`);
  }
  const raw: GuessZapResponse = await response.json();
  const data: ZapGuess = {
    title: raw.title,
    prefilledUrl: raw.prefilled_url,
    steps: raw.steps.map((s) => ({
      title: s.step.title,
      appName: s.step.app,
      selectedApi: s.step.api,
    })),
  };
  return data;
}

export type AppIconSizes = 128 | 64 | 32 | 16;

export function getImageUrlForSelectedApi({
  selectedApi,
  size,
}: {
  selectedApi: string;
  size: AppIconSizes;
}): string {
  return `https://zapier.com/generated/${selectedApi}/${size}`;
}
